import { useState, useEffect, useCallback } from 'react';
import { getAdminStats, getAdminUsers, seedPeethsAdmin } from '../services/api';
import { triggerToast } from '../components/Toast';
import BackButton from '../components/BackButton';
import { useLang, TOTAL_PEETHS } from '../i18n';

function formatNum(n) {
  if (n >= 100000) return (n / 100000).toFixed(1).replace(/\.0$/, '') + ' लाख';
  return n.toLocaleString('en-IN');
}

// Inline SVG icon set — replaces emoji icons with themed glyphs
function StatIcon({ kind }) {
  const stroke = 'var(--saffron-dark)';
  const common = { width: 22, height: 22, viewBox: '0 0 24 24', fill: 'none', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round' };
  switch (kind) {
    case 'users': return (
      <svg {...common} stroke={stroke}>
        <circle cx="9" cy="8" r="3.2" /><path d="M3 20c1-3 3.5-5 6-5s5 2 6 5" />
        <circle cx="17" cy="9" r="2.4" /><path d="M15.5 14c1.5 0 4 1 5 4" />
      </svg>
    );
    case 'chants': return (
      <svg {...common} stroke={stroke}>
        <path d="M12 3 C9 7 9 11 12 15 C15 11 15 7 12 3 Z" />
        <path d="M5 14 C7 13 9 13 12 15" /><path d="M19 14 C17 13 15 13 12 15" />
      </svg>
    );
    case 'sessions': return (
      <svg {...common} stroke={stroke}>
        <rect x="3" y="6" width="18" height="13" rx="2.5" />
        <path d="M8 11h.01M16 11h.01M11 14h2" />
      </svg>
    );
    case 'peeths': return (
      <svg {...common} stroke={stroke}>
        <path d="M4 21V11 L12 4 L20 11 V21" /><path d="M9 21V14h6v7" />
        <path d="M12 4 L12 2" /><circle cx="12" cy="9" r="0.8" fill={stroke} />
      </svg>
    );
    default: return null;
  }
}

export default function AdminScreen({ onBack }) {
  const { t } = useLang();
  const [tab, setTab] = useState('stats');
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [usersTotal, setUsersTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);

  const loadStats = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getAdminStats();
      setStats(data);
    } catch {
      triggerToast(t('admin.statsLoadFailed'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  const loadUsers = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getAdminUsers(page);
      setUsers(data.users || []);
      setUsersTotal(data.total || 0);
    } catch {
      triggerToast(t('admin.usersLoadFailed'));
    } finally {
      setLoading(false);
    }
  }, [page, t]);

  useEffect(() => { loadStats(); }, [loadStats]);
  useEffect(() => { if (tab === 'users') loadUsers(); }, [tab, page, loadUsers]);

  async function handleSeed() {
    setSeeding(true);
    try {
      const data = await seedPeethsAdmin();
      triggerToast(t('admin.seedResult', { inserted: data.inserted, skipped: data.skipped }));
    } catch (err) {
      triggerToast(t('admin.seedFailed', { error: err.message }));
    } finally {
      setSeeding(false);
    }
  }

  return (
    <div className="screen admin-screen" style={{ padding: 0, overflow: 'hidden' }}>
      {/* Header */}
      <div className="screen-header">
        <BackButton onClick={onBack} />
        <div style={{ flex: 1 }}>
          <div className="screen-header-title">{t('admin.title')}</div>
          <div className="screen-header-sub">{t('admin.subtitle')}</div>
        </div>
        <div className="admin-badge-chip">ADMIN</div>
      </div>

      {/* Segmented Tabs */}
      <div className="admin-segment">
        {[
          { id: 'stats', label: t('admin.tabStats') },
          { id: 'users', label: t('admin.tabUsers') },
          { id: 'tools', label: t('admin.tabTools') },
        ].map(tb => (
          <button
            key={tb.id}
            className={`admin-seg-btn ${tab === tb.id ? 'active' : ''}`}
            onClick={() => setTab(tb.id)}
          >
            <span className="admin-seg-hi">{tb.label}</span>
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="admin-scroll">
        {loading && (
          <div style={{ textAlign: 'center', padding: 40 }}>
            <div className="spinner" />
          </div>
        )}

        {/* ── STATS TAB ─────────────────────────────────────── */}
        {!loading && tab === 'stats' && stats && (
          <>
            <div className="admin-stats-grid">
              {[
                { kind: 'users',    label: t('admin.statTotalUsers'),    value: stats.total_users || 0,         tone: 'saffron' },
                { kind: 'chants',   label: t('admin.statTotalChants'),   value: stats.total_ram_count || 0,     tone: 'gold' },
                { kind: 'sessions', label: t('admin.statTotalSessions'), value: stats.total_sessions || 0,      tone: 'maroon' },
                { kind: 'peeths',   label: t('admin.statPeethUnlocks'),  value: stats.total_peeth_unlocks || 0, tone: 'saffron' },
              ].map((s, i) => (
                <div key={s.kind} className="admin-stat-card" style={{ '--i': i }} data-tone={s.tone}>
                  <div className="admin-stat-icon"><StatIcon kind={s.kind} /></div>
                  <div className="admin-stat-value">{formatNum(s.value)}</div>
                  <div className="admin-stat-label-hi">{s.label}</div>
                </div>
              ))}
            </div>

            {/* Daily / Monthly Active */}
            {(stats.dau != null || stats.mau != null) && (
              <div className="admin-section">
                <div className="admin-section-title">{t('admin.activeUsers')}</div>
                <div className="admin-active-row">
                  <div className="admin-active-item">
                    <div className="admin-active-num">{formatNum(stats.dau || 0)}</div>
                    <div className="admin-active-cap">{t('admin.dau')}</div>
                  </div>
                  <div className="admin-active-divider" />
                  <div className="admin-active-item">
                    <div className="admin-active-num">{formatNum(stats.mau || 0)}</div>
                    <div className="admin-active-cap">{t('admin.mau')}</div>
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {/* ── USERS TAB ─────────────────────────────────────── */}
        {!loading && tab === 'users' && (
          <>
            <div className="admin-section-header">
              <div className="admin-section-title">{t('admin.usersTotal', { n: usersTotal })}</div>
              <div className="admin-section-meta">{t('admin.page', { n: page })}</div>
            </div>
            <div className="admin-users-list">
              {users.map((u, idx) => {
                const initial = (u.name || '?').trim().charAt(0).toUpperCase();
                return (
                  <div key={u.user_id} className="admin-user-row" style={{ '--i': idx }}>
                    <div className={`admin-user-avatar ${u.role === 'admin' ? 'is-admin' : ''}`}>
                      {initial}
                    </div>
                    <div className="admin-user-body">
                      <div className="admin-user-name">
                        {u.name}
                        {u.role === 'admin' && <span className="admin-role-pill">ADMIN</span>}
                      </div>
                      <div className="admin-user-email">{u.email}</div>
                    </div>
                    <div className="admin-user-stats">
                      <div className="admin-user-ram">{formatNum(u.total_ram_count || 0)}</div>
                      <div className="admin-user-peeths">{t('admin.peethsCount', { n: u.shakti_peeths_visited || 0, total: TOTAL_PEETHS })}</div>
                    </div>
                  </div>
                );
              })}
              {users.length === 0 && (
                <div className="admin-empty">{t('admin.noUsers')}</div>
              )}
            </div>

            <div className="admin-pagination">
              <button
                className="admin-page-btn"
                disabled={page <= 1}
                onClick={() => setPage(p => Math.max(1, p - 1))}
              >{t('admin.prev')}</button>
              <div className="admin-page-current">{page}</div>
              <button
                className="admin-page-btn"
                disabled={users.length < 20}
                onClick={() => setPage(p => p + 1)}
              >{t('admin.next')}</button>
            </div>
          </>
        )}

        {/* ── TOOLS TAB ─────────────────────────────────────── */}
        {!loading && tab === 'tools' && (
          <div className="admin-tools-list">
            <div className="admin-tool-card" style={{ '--i': 0 }}>
              <div className="admin-tool-header">
                <div className="admin-tool-icon"><StatIcon kind="peeths" /></div>
                <div>
                  <div className="admin-tool-title">{t('admin.toolSeedTitle', { total: TOTAL_PEETHS })}</div>
                  <div className="admin-tool-sub">{t('admin.toolSeedSub', { total: TOTAL_PEETHS })}</div>
                </div>
              </div>
              <p className="admin-tool-desc">
                {t('admin.toolSeedDesc', { total: TOTAL_PEETHS })}<br/>
                <span style={{ opacity: 0.7 }}>{t('admin.toolSeedNote')}</span>
              </p>
              <button
                className="admin-tool-btn"
                onClick={handleSeed}
                disabled={seeding}
              >
                {seeding ? (
                  <>
                    <span className="admin-tool-spinner" />
                    {t('admin.seeding')}
                  </>
                ) : t('admin.runSeed')}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
